import { getPreSignedUrl } from '@/services/auth/presignedUrl.queries';
import axios, { AxiosError } from 'axios';
import { transformImageUrl } from './transformImageUrl';
import type { ImageFileObject } from './uploadSchema';

/**
 * Represents the backend response structure for the pre-signed URL API.
 */

/**
 * Extracts the file name from a File object.
 *
 * @param file - The file object.
 * @returns The file name.
 */
const extractFileName = (file: { file: File }): string => {
  return file.file.name;
};

/**
 * Uploads a file to S3 using a pre-signed URL generated by the backend.
 *
 * @param selectedFile - The file to be uploaded.
 * @returns The publicly accessible URL of the uploaded file.
 * @throws Error when the URL cannot be retrieved or the upload fails.
 */

export type FileObject = ImageFileObject;

export const uploadFileToS3WithPresignedUrl = async (selectedFile: FileObject): Promise<string> => {
  try {
    const fileName = extractFileName(selectedFile);
    const cleanFileName = encodeURIComponent(fileName);

    // Request presigned URL
    const response = await getPreSignedUrl(cleanFileName);
    const responseData = response.data as { data?: string };

    if (!responseData?.data) {
      throw new Error('Invalid response from backend: Missing pre-signed URL.');
    }

    const presignedUrl = responseData.data;

    // Upload ONLY the raw file
    await axios.put(presignedUrl, selectedFile.file, {
      headers: {
        'Content-Type': selectedFile.file.type,
        'Cache-Control': 'no-cache, max-age=0, must-revalidate',
      },
    });

    // Final URL without query params
    const fileUrl = presignedUrl.split('?')[0];

    const cleanedUrl = transformImageUrl(fileUrl) as string;

    return cleanedUrl;
  } catch (err) {
    const error = err as AxiosError;
    throw new Error(error.message || 'Failed to upload file using pre-signed URL.');
  }
};
